import { useState } from "react";
import {
  useGetInvoice, getGetInvoiceQueryKey,
  useMarkInvoicePaid, getListInvoicesQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { Lock, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";

interface Props {
  invoiceId: number;
  onClose: () => void;
}

export function MarkPaidDialog({ invoiceId, onClose }: Props) {
  const qc = useQueryClient();
  const { data: inv } = useGetInvoice(invoiceId, {
    query: { queryKey: getGetInvoiceQueryKey(invoiceId) },
  });
  const [paymentMethod, setPaymentMethod] = useState<"Cash" | "Bank_Transfer" | "Cheque" | "UPI_Manual">("Cash");
  const [amountPaid, setAmountPaid] = useState<string>("");
  const [transactionRef, setTransactionRef] = useState<string>("");
  const [paymentDate, setPaymentDate] = useState<string>(new Date().toISOString().slice(0, 10));

  const due = inv ? Number(inv.total) - Number(inv.amountPaid) : 0;

  const mutation = useMarkInvoicePaid({
    mutation: {
      onSuccess: () => {
        toast.success("Payment recorded");
        qc.invalidateQueries({ queryKey: getListInvoicesQueryKey() });
        qc.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
        onClose();
      },
      onError: (err) => toast.error(`Mark Paid failed: ${(err as Error).message}`),
    },
  });

  function submit() {
    const n = Number(amountPaid);
    if (!Number.isFinite(n) || n <= 0) { toast.error("Enter a valid amount"); return; }
    if (n > due) { toast.error(`Amount cannot exceed amount due of Rs. ${due.toFixed(2)}`); return; }
    mutation.mutate({
      id: invoiceId,
      data: { paymentMethod, amountPaid: n, transactionRef: transactionRef || null, paymentDate },
    });
  }

  const upiUri = inv ? `upi://pay?pa=hoblix@upi&pn=Hoblix&am=${due.toFixed(2)}&tn=${inv.invoiceNumber}` : "";

  return (
    <Tabs defaultValue="manual">
      <TabsList className="grid w-full grid-cols-3">
        <TabsTrigger value="manual">Manual Entry</TabsTrigger>
        <TabsTrigger value="upi">UPI Screenshot</TabsTrigger>
        <TabsTrigger value="gateway" disabled>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex items-center gap-1">Gateway <Lock className="h-3 w-3" /></span>
              </TooltipTrigger>
              <TooltipContent>Razorpay integration coming soon</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </TabsTrigger>
      </TabsList>

      <TabsContent value="manual" className="space-y-3">
        <div>
          <Label>Payment Method</Label>
          <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value as typeof paymentMethod)}
            className="w-full border rounded h-10 px-2">
            <option value="Cash">Cash</option>
            <option value="Bank_Transfer">Bank Transfer</option>
            <option value="Cheque">Cheque</option>
            <option value="UPI_Manual">UPI (Manual)</option>
          </select>
        </div>
        <div>
          <Label>Amount Paid (Rs.)</Label>
          <Input type="number" step="0.01" value={amountPaid} onChange={(e) => setAmountPaid(e.target.value)} placeholder={due.toFixed(2)} />
          <div className="text-xs text-muted-foreground mt-1">Due: Rs. {due.toFixed(2)}</div>
        </div>
        <div>
          <Label>Transaction Reference (optional)</Label>
          <Input value={transactionRef} onChange={(e) => setTransactionRef(e.target.value)} placeholder="UTR / cheque #" />
        </div>
        <div>
          <Label>Payment Date</Label>
          <Input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={mutation.isPending}>
            {mutation.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
            Record Payment
          </Button>
        </div>
      </TabsContent>

      <TabsContent value="upi" className="space-y-3">
        <div>
          <Label>UPI Payment Link</Label>
          <div className="flex gap-2">
            <Input readOnly value={upiUri} className="font-mono text-xs" />
            <Button size="icon" variant="outline"
              onClick={() => { navigator.clipboard.writeText(upiUri); toast.success("Copied"); }}>
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">Send this link to the member; they pay via UPI app, then you upload their screenshot here. Recording the payment uses the same form as Manual Entry below.</p>
        </div>
        <p className="text-sm">Once paid, switch to Manual Entry tab and record with UPI_Manual payment method + transaction ref from screenshot.</p>
      </TabsContent>

      <TabsContent value="gateway">
        <div className="text-sm text-muted-foreground py-4">Razorpay integration coming soon.</div>
      </TabsContent>
    </Tabs>
  );
}
